import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { usePlaceBulkUploadPaymentMutation } from '../../services/orderApi';
import { updatesKeyOfBulkOrderCartData } from '../../cart/utils/cartTransforms';
import { useNavigationService } from '../../../../services/navigate-service';
import { ROUTES } from '../../../../config/routes';
import { clearOrderData } from '../../order-confirmation/order-confirmation-store/orderSlice';
import type { SavedCard } from '../../../../types/order.types';
import { buildOrderPayment } from '@/util/orderPayment';
import { useSkipCheckout } from '@/hooks/useEntityScope';

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useBulkImportOrder() {
  const dispatch = useDispatch();
  const { navigateTo } = useNavigationService();
  const [placeBulkUploadPayment] = usePlaceBulkUploadPaymentMutation();

  const isLogin = localStorage.getItem('isLogin');
  const skipCheckout = useSkipCheckout();
  const isSkipCheckOut = isLogin === null ? null : skipCheckout;

  // Step state
  const [activeStep, setActiveStep] = useState(1);

  // File upload state (types match BulkImportCallbacks)
  const [fileId, setFileId] = useState<string | null>(null);
  const [uploadedByInfo, setUploadedByInfo] = useState<string | undefined>();
  const [fileName, setFileName] = useState<string | undefined>();
  const [dateInfo, setDateInfo] = useState<string | undefined>();
  const [fileState, setFileState] = useState<boolean | null>(null);
  const [fileNumber, setFileNumber] = useState<number | undefined>();
  const [fileUploadDisabled, setFileUploadDisabled] = useState(false);

  // Table data state (types match BulkImportCallbacks: string | null)
  const [tableData, setTableData] = useState<string | null>(null);
  const [successData, setSuccessData] = useState<string | null>(null);
  const [failedData, setFailedData] = useState<string | null>(null);
  const [fixErrorTableData, setFixErrorTableData] = useState<string | null>(
    null,
  );
  const [fromTableDataFromNextPage, setFromTableDataFromNextPage] =
    useState(false);

  // Order / amount state
  const [orderCountInfo, setOrderCountInfo] = useState<
    string | number | undefined
  >();
  const [amount, setAmount] = useState<number | undefined>();
  const [showStep3, setShowStep3] = useState(false);
  const [isContinue, setIsContinue] = useState(true);

  // Payment state — `Partial<SavedCard>` matches the shape PaymentPage flows up
  // (`payment.selectedCard ?? {}`); consumers below pull the card-payment fields
  // via optional chaining and tolerate missing values.
  const [paymentInfo, setPaymentInfo] = useState<Partial<SavedCard> | undefined>();
  const [promoCodeFromPaymentBulkOrder, setPromoCodeFromPaymentBulkOrder] =
    useState<string | null>(null);
  const [callSubmitOrder, setCallSubmitOrders] = useState(false);
  const [savingsAmountFromPayment, setSavingsAmountFromPayment] =
    useState<number>(0);
  const [paymentLoader, setPaymentLoader] = useState(false);
  const [paymentFailedLoader, setPaymentFailedLoader] = useState(false);

  // Stepper skip state
  const [skippedActiveStep, setSkippedActiveStep] = useState<
    number | undefined
  >();
  const [skippedStepFromStepper, setSkippedStepFromStepper] = useState<
    number | undefined
  >();

  // -----------------------------------------------------------------------
  // Submit order on callSubmitOrder flag
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (callSubmitOrder) {
      submitBulkOrder();
    }
    // Fires only when the submit flag flips true; submitBulkOrder reads its
    // closure for the latest values rather than re-running per-prop change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callSubmitOrder]);

  const submitBulkOrder = async () => {
    const requestObj: Record<string, unknown> =
      isSkipCheckOut || amount === 0
        ? {}
        : {
            // Bulk-import carries the payment fields flat (not nested under `payment`) plus
            // cardNumber/cardType. Only spread the profile ids when a card is actually selected;
            // with no card, keep the pre-DV-2804 behaviour of omitting them (undefined → dropped by
            // JSON.stringify) rather than sending empty strings, so order-service still sees the
            // no-payment shape it did before (DV-2804 review).
            ...(paymentInfo?.customerProfileId
              ? buildOrderPayment(paymentInfo)
              : { tax: 0 }),
            cardNumber: paymentInfo?.cardNumber,
            cardType: paymentInfo?.cardType,
          };

    setPaymentLoader(true);
    try {
      if (!fileId) throw new Error('Missing fileId for bulk upload payment');
      const data = await placeBulkUploadPayment({
        fileId,
        promocode: promoCodeFromPaymentBulkOrder,
        body: requestObj,
      }).unwrap();
      setTableData(null);
      const bulkOrderCartResponseData = updatesKeyOfBulkOrderCartData(
        'items',
        data.orders,
      );
      setCallSubmitOrders(false);
      setPaymentLoader(false);
      // The bulk path threads data via location.state rather than Redux,
      // so any prior single-order confirmation data lingering in the
      // slice would briefly bleed into the confirmation page before the
      // location-state effect overwrote it. Clear the slice first.
      dispatch(clearOrderData());
      navigateTo(ROUTES.ORDERS_CONFIRMATION, {}, {
        bulkOrderResponseData: bulkOrderCartResponseData,
        isBulkOrder: true,
      });
    } catch {
      setCallSubmitOrders(false);
      setPaymentLoader(false);
      setPaymentFailedLoader(true);
    }
  };

  // -----------------------------------------------------------------------
  // Cancel
  // -----------------------------------------------------------------------

  const onCancel = useCallback(() => {
    navigateTo(ROUTES.ORDERS);
  }, [navigateTo]);

  return {
    // step
    activeStep,
    setActiveStep,
    skippedActiveStep,
    setSkippedActiveStep,
    skippedStepFromStepper,
    setSkippedStepFromStepper,

    // file upload
    fileId,
    setFileId,
    uploadedByInfo,
    setUploadedByInfo,
    fileName,
    setFileName,
    dateInfo,
    setDateInfo,
    fileState,
    setFileState,
    fileNumber,
    setFileNumber,
    fileUploadDisabled,
    setFileUploadDisabled,

    // table data
    tableData,
    setTableData,
    successData,
    setSuccessData,
    failedData,
    setFailedData,
    fixErrorTableData,
    setFixErrorTableData,
    fromTableDataFromNextPage,
    setFromTableDataFromNextPage,

    // order / amount
    orderCountInfo,
    setOrderCountInfo,
    amount,
    setAmount,
    showStep3,
    setShowStep3,
    isContinue,
    setIsContinue,

    // payment
    paymentInfo,
    setPaymentInfo,
    promoCodeFromPaymentBulkOrder,
    setPromoCodeFromPaymentBulkOrder,
    callSubmitOrder,
    setCallSubmitOrders,
    savingsAmountFromPayment,
    setSavingsAmountFromPayment,
    paymentLoader,
    setPaymentLoader,
    paymentFailedLoader,
    setPaymentFailedLoader,

    // actions
    onCancel,
  };
}
